// src/pages/PresetsPage.tsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Upload,
  Edit2,
  Trash2,
  Copy,
  Eye,
  Tag,
  BarChart,
  FileText,
  Mail,
  MessageSquare,
  MoreHorizontal
} from 'lucide-react';

interface Template {
  id: number;
  name: string;
  category: string;
  content: string;
  variables: string[];
  tags: string[];
  usage_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
}

const PresetsPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});
  const [stats, setStats] = useState<any>(null);

  // Formularz dla nowego/edycji szablonu
  const [formData, setFormData] = useState({
    name: '',
    category: 'wiadomości',
    content: '',
    variables: [] as string[],
    tags: [] as string[]
  });

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.plexisystem.pl';

  useEffect(() => {
    loadTemplates();
    loadCategories();
    loadStats();
  }, [selectedCategory, searchQuery]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/templates?active=true`;
      
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error('API Error:', response.status);
        setTemplates([]);
        return;
      }
      
      const data = await response.json();
      
      // Upewnij się, że data jest tablicą
      const templatesArray = Array.isArray(data) ? data : [];
      
      // Filtruj lokalnie po searchQuery
      const filtered = searchQuery 
        ? templatesArray.filter((t: Template) => 
            t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (Array.isArray(t.tags) && t.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
          )
        : templatesArray;
      
      setTemplates(filtered);
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates/categories`);
      
      if (!response.ok) {
        console.error('API Error:', response.status);
        return;
      }
      
      const data = await response.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates/stats`);
      
      if (!response.ok) {
        console.error('API Error:', response.status);
        setStats(null);
        return;
      }
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = editingTemplate 
        ? `${API_URL}/api/templates/${editingTemplate.id}`
        : `${API_URL}/api/templates`;
      
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setShowModal(false);
        setEditingTemplate(null);
        resetForm();
        loadTemplates();
        loadStats();
      } else {
        const error = await response.json();
        alert(`Błąd: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Błąd podczas zapisywania szablonu');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Czy na pewno chcesz usunąć ten szablon?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/templates/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        loadTemplates();
        loadStats();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      content: template.content,
      variables: template.variables,
      tags: template.tags
    });
    setShowModal(true);
  };

  const handlePreview = async (template: Template) => {
    setPreviewTemplate(template);
    const vars: Record<string, string> = {};
    template.variables.forEach(v => {
      vars[v] = '';
    });
    setPreviewVariables(vars);
    setShowPreview(true);
  };

  const handleUseTemplate = async (template: Template) => {
    try {
      await fetch(`${API_URL}/api/templates/${template.id}/use`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          used_in: 'web_interface',
          variables_used: previewVariables
        })
      });
      
      // Skopiuj do schowka
      const content = replaceVariables(template.content, previewVariables);
      navigator.clipboard.writeText(content);
      alert('Szablon skopiowany do schowka!');
      
      setShowPreview(false);
      loadTemplates();
      loadStats();
    } catch (error) {
      console.error('Error using template:', error);
    }
  };

  const replaceVariables = (content: string, variables: Record<string, string>) => {
    let result = content;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      result = result.replace(regex, value || `{${key}}`);
    });
    return result;
  };

  const extractVariables = (content: string): string[] => {
    const matches = content.match(/\{([^}]+)\}/g);
    if (!matches) return [];
    
    const variables = matches.map(m => m.slice(1, -1));
    return [...new Set(variables)];
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'wiadomości',
      content: '',
      variables: [],
      tags: []
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'oferty': return <FileText className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'wiadomości': return <MessageSquare className="w-4 h-4" />;
      default: return <MoreHorizontal className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white">Szablony odpowiedzi</h1>
              <p className="text-gray-400 mt-2">
                Zarządzaj szablonami wiadomości, ofert i emaili
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setEditingTemplate(null);
                setShowModal(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nowy szablon
            </button>
          </div>

          {/* Statystyki */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-800 border border-gray-700 p-4 rounded-lg">
                <div className="text-2xl font-bold text-white">{stats.totalTemplates}</div>
                <div className="text-sm text-gray-400">Wszystkie szablony</div>
              </div>
              {stats.byCategory?.map((cat: any) => (
                <div key={cat.category} className="bg-gray-800 border border-gray-700 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-white">{cat.count}</div>
                  <div className="text-sm text-gray-400 capitalize">{cat.category}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filtry */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Szukaj szablonów..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === '' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                }`}
              >
                Wszystkie
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    selectedCategory === cat.id 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                  }`}
                >
                  <span>{cat.icon}</span>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Lista szablonów */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Brak szablonów do wyświetlenia</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {templates.map(template => (
              <div key={template.id} className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-gray-600 transition-colors">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">
                        {template.name}
                      </h3>
                      <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full flex items-center gap-1">
                        {getCategoryIcon(template.category)}
                        {categories.find(c => c.id === template.category)?.name || template.category}
                      </span>
                      <span className="text-sm text-gray-500">
                        Użyto: {template.usage_count} razy
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {template.tags.map(tag => (
                        <span key={tag} className="px-2 py-1 bg-blue-900/30 text-blue-400 text-xs rounded">
                          #{tag}
                        </span>
                      ))}
                      {template.variables.length > 0 && (
                        <span className="px-2 py-1 bg-green-900/30 text-green-400 text-xs rounded">
                          {template.variables.length} zmiennych
                        </span>
                      )}
                    </div>
                    <div className="text-gray-400 text-sm whitespace-pre-wrap line-clamp-3">
                      {template.content}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handlePreview(template)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                      title="Podgląd"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(template.content);
                        alert('Skopiowano do schowka!');
                      }}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                      title="Kopiuj"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                      title="Edytuj"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors"
                      title="Usuń"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal dodawania/edycji */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4 text-white">
                  {editingTemplate ? 'Edytuj szablon' : 'Nowy szablon'}
                </h2>
                
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nazwa szablonu
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Kategoria
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Treść szablonu
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => {
                        const content = e.target.value;
                        const variables = extractVariables(content);
                        setFormData({ ...formData, content, variables });
                      }}
                      rows={10}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                      placeholder="Użyj {nazwa_zmiennej} dla zmiennych, np. {imie}, {firma}, {data}"
                      required
                    />
                    {formData.variables.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm text-gray-400">Wykryte zmienne: </span>
                        {formData.variables.map(v => (
                          <span key={v} className="inline-block px-2 py-1 bg-green-900/30 text-green-400 text-xs rounded ml-1">
                            {v}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Tagi (oddzielone przecinkami)
                    </label>
                    <input
                      type="text"
                      value={formData.tags.join(', ')}
                      onChange={(e) => {
                        const tags = e.target.value
                          .split(',')
                          .map(t => t.trim())
                          .filter(t => t);
                        setFormData({ ...formData, tags });
                      }}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                      placeholder="np. powitanie, oferta, podziękowanie"
                    />
                  </div>

                  <div className="flex justify-end gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingTemplate(null);
                        resetForm();
                      }}
                      className="px-4 py-2 text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Anuluj
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {editingTemplate ? 'Zapisz zmiany' : 'Dodaj szablon'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal podglądu */}
        {showPreview && previewTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4 text-white">
                  Podgląd: {previewTemplate.name}
                </h2>
                
                {previewTemplate.variables.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3 text-gray-300">Wypełnij zmienne:</h3>
                    <div className="grid gap-3">
                      {previewTemplate.variables.map(variable => (
                        <div key={variable}>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            {variable}
                          </label>
                          <input
                            type="text"
                            value={previewVariables[variable] || ''}
                            onChange={(e) => setPreviewVariables({
                              ...previewVariables,
                              [variable]: e.target.value
                            })}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                            placeholder={`Wprowadź ${variable}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-semibold mb-3 text-gray-300">Podgląd treści:</h3>
                  <div className="p-4 bg-gray-900 border border-gray-700 rounded-lg whitespace-pre-wrap text-gray-300">
                    {replaceVariables(previewTemplate.content, previewVariables)}
                  </div>
                </div>

                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => {
                      setShowPreview(false);
                      setPreviewTemplate(null);
                      setPreviewVariables({});
                    }}
                    className="px-4 py-2 text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Zamknij
                  </button>
                  <button
                    onClick={() => handleUseTemplate(previewTemplate)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Użyj szablonu
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PresetsPage;
